import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { Alert, Button, Col, DatePicker, Row, Segmented, Select, Skeleton, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import {
  DollarCircleOutlined,
  ThunderboltOutlined,
  PlayCircleOutlined,
  DashboardOutlined,
  DownloadOutlined,
  WarningFilled,
  ShopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { KpiTile } from '@/components/KpiTile';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { MiniBar, StackedColumns, BandHistogram } from '@/features/insights/InsightsViz';
import type { AiSpendAgentRow, AiSpendReport } from '@/api/aiSpend';
import type { CostsRange } from '@/stores/CostsStore';
import { AgentSpendDrawer, GrainSwitch, RunCallsDrawer, RunTable } from './CostsDrawers';
import { compact, count, dayLabel, modelColor, monthLabel, usd, usdCents } from './costsFormat';

const { Text } = Typography;

// Agent Studio → Watch → Costs. What every AI call cost, by day, agent and model —
// and, before any of that, whether the number is complete. Every figure comes
// from api/AiSpend (AiSpendRules on the server); this page only lays it out.

export const CostsPage = observer(function CostsPage() {
  const { costsStore: s } = useStore();

  useEffect(() => {
    if (!s.report && !s.loading) void s.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const r = s.report;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        accent="amber"
        icon={<DollarCircleOutlined />}
        title="Costs"
        subtitle="What every AI call cost, by day, agent and model"
        helpTopic="costs.overview"
        actions={<Controls />}
      />

      {s.error && <Alert type="error" showIcon message="Costs could not be loaded" description={s.error} />}

      {!r && s.loading && <Skeleton active paragraph={{ rows: 10 }} />}

      {r && (
        <Spin spinning={s.loading}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <CoverageStrip r={r} />
            <Headline r={r} />
            {r.totals.calls === 0 ? (
              <SectionCard>
                <BrandedEmpty icon={<DollarCircleOutlined />} title="No AI calls in this range" description={`Nothing was logged between ${dayLabel(r.window.from, true)} and ${dayLabel(r.window.to, true)} for this filter.`} />
              </SectionCard>
            ) : (
              <>
                <Trend r={r} />
                <Row gutter={[16, 16]}>
                  <Col xs={24} xl={15}>
                    <Agents r={r} onOpen={(id) => void s.openAgent(id)} />
                  </Col>
                  <Col xs={24} xl={9}>
                    <PerRun r={r} />
                  </Col>
                </Row>
                <Unusual r={r} />
              </>
            )}
          </div>
        </Spin>
      )}

      {/* Open while loading, showing or explaining a failure; a run opens on top of the agent drawer. */}
      <AgentSpendDrawer open={s.agentLoading || s.agentDetail != null || s.agentError != null} onClose={() => s.closeAgent()} />
      <RunCallsDrawer open={s.runLoading || s.runDetail != null || s.runError != null} onClose={() => s.closeRun()} />
    </div>
  );
});

// ── Controls: range · merchant · export ─────────────────────────────────────

const Controls = observer(function Controls() {
  const { costsStore: s, merchantContextStore: ctx } = useStore();
  const [msg, holder] = message.useMessage();
  const exportCsv = async () => {
    try {
      await s.exportCsv();
    } catch (err) {
      void msg.error(err instanceof Error ? err.message : 'CSV export failed');
    }
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {holder}
      <Segmented<CostsRange>
        value={s.range}
        onChange={(v) => (v === 'custom' ? s.pickCustom() : s.setRange(v))}
        options={[
          { label: '7 days', value: '7' },
          { label: '30 days', value: '30' },
          { label: '90 days', value: '90' },
          { label: 'This month', value: 'month' },
          { label: 'Custom', value: 'custom' },
        ]}
      />
      {s.range === 'custom' && (
        <DatePicker.RangePicker
          allowClear={false}
          value={s.customFrom && s.customTo ? [dayjs(s.customFrom), dayjs(s.customTo)] : null}
          onChange={(v) => v?.[0] && v?.[1] && s.setCustom(v[0].format('YYYY-MM-DD'), v[1].format('YYYY-MM-DD'))}
          disabledDate={(d) => d.isAfter(dayjs(), 'day')}
        />
      )}
      <Select<number | 'all'>
        value={s.merchantId ?? 'all'}
        onChange={(v) => s.setMerchant(v === 'all' ? null : v)}
        style={{ minWidth: 200 }}
        suffixIcon={<ShopOutlined />}
        showSearch
        optionFilterProp="label"
        options={[{ label: 'All merchants', value: 'all' as const }, ...ctx.merchants.map((m) => ({ label: m.title, value: m.id }))]}
      />
      <Button icon={<DownloadOutlined />} loading={s.exporting} onClick={() => void exportCsv()} disabled={!s.report}>
        Export CSV
      </Button>
    </div>
  );
});

// ── Is the number complete? ─────────────────────────────────────────────────

const CoverageStrip = observer(function CoverageStrip({ r }: { r: AiSpendReport }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const c = r.coverage;
  if (c.callsMissingCost === 0) return null;
  const pct = r.totals.calls > 0 ? ((c.callsMissingCost / r.totals.calls) * 100).toFixed(1) : '0';
  const chips: Array<{ key: string; text: string; tip: string }> = [];
  for (const m of c.byModel) {
    if (m.noRateCalls > 0)
      chips.push({
        key: m.model + '-norate',
        text: `${m.rateOnFileNow ? 'Rate added since' : 'No rate on file'} · ${m.model} ${count(m.noRateCalls)}`,
        tip: m.rateOnFileNow
          ? 'A rate exists today, but these calls are older than it or it did not match when they were written.'
          : 'Model pricing has no active rate for this model, so no cost was stored.',
      });
    if (m.storedAsZeroCalls > 0)
      chips.push({
        key: m.model + '-zero',
        text: `Stored as $0.00 · ${m.model} ${count(m.storedAsZeroCalls)}`,
        tip: 'Tokens were used but $0.00 was stored. Before 7 Sep 2026 costs were rounded to whole cents.',
      });
  }
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        padding: '12px 16px',
        borderRadius: 12,
        background: isDark ? colors.amber[700] + '26' : '#FFFBEB',
        border: `1px solid ${isDark ? colors.amber[700] : '#FDE68A'}`,
        fontSize: 13.5,
        lineHeight: 1.55,
      }}
    >
      <WarningFilled style={{ color: colors.amber[600], fontSize: 17, marginTop: 3 }} />
      <div style={{ flex: 1 }}>
        <b>
          The spend shown is a floor: {count(c.callsMissingCost)} of {count(r.totals.calls)} calls ({pct}%) are missing their cost.
        </b>{' '}
        The page never guesses a price for them; they are named here so the gap can be closed.
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 4px' }}>
          {chips.map((ch) => (
            <Tooltip key={ch.key} title={ch.tip}>
              <Tag color="gold" style={{ marginInlineEnd: 0 }}>{ch.text}</Tag>
            </Tooltip>
          ))}
        </div>
        <Link to="/pricing" style={{ fontWeight: 600 }}>Add rates in Model pricing ›</Link>
        <SectionHelp topic="costs.coverage" />
      </div>
    </div>
  );
});

// ── Headline ────────────────────────────────────────────────────────────────

const Headline = observer(function Headline({ r }: { r: AiSpendReport }) {
  const t = r.totals;
  const diff = t.spendUsd - t.previousSpendUsd;
  const prevLabel = `prior ${r.window.days} days (${usdCents(t.previousSpendUsd)})`;
  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} sm={12} xl={6}>
        <KpiTile
          label="Spend"
          icon={<DollarCircleOutlined />}
          accent="amber"
          value={usdCents(t.spendUsd)}
          hint={`prior period: ${dayLabel(r.window.previousFrom)} – ${dayLabel(r.window.previousTo)}`}
          helpTopic="costs.overview"
          trend={{
            direction: Math.abs(diff) < 0.005 ? 'flat' : diff > 0 ? 'up' : 'down',
            label: `${diff >= 0 ? '+' : '−'}${usdCents(Math.abs(diff))} vs ${prevLabel}`,
            goodWhen: 'neither',
          }}
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiTile label="AI calls" icon={<ThunderboltOutlined />} value={count(t.calls)} hint={`${compact(t.inputTokens)} tokens in · ${compact(t.outputTokens)} out`} />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiTile label="Runs" icon={<PlayCircleOutlined />} value={count(t.runs)} hint="one request: a chat turn or one workflow pass" helpTopic="costs.per-run" />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiTile
          label="Cost per run"
          icon={<DashboardOutlined />}
          value={r.runStats.completeRuns > 0 ? usd(r.runStats.medianUsd) : '—'}
          hint={r.runStats.completeRuns > 0 ? `median · P95 ${usd(r.runStats.p95Usd)} · highest ${usd(r.runStats.maxUsd)}` : 'no fully priced runs in this range'}
          helpTopic="costs.per-run"
        />
      </Col>
    </Row>
  );
});

// ── Spend over time ─────────────────────────────────────────────────────────

const Trend = observer(function Trend({ r }: { r: AiSpendReport }) {
  const { costsStore: s } = useStore();
  const periods = r.trend[s.grain];
  const peak = periods.reduce((best, p) => (p.usd > (best?.usd ?? -1) ? p : best), periods[0]);
  const label = (start: string) => (s.grain === 'month' ? monthLabel(start) : dayLabel(start));
  const title = (start: string) => (s.grain === 'month' ? monthLabel(start) : s.grain === 'week' ? `Week of ${dayLabel(start, true)}` : dayLabel(start, true));

  return (
    <SectionCard
      title="Spend over time"
      subtitle={peak && peak.usd > 0 ? `Stacked by model. Highest ${s.grain}: ${title(peak.start)}, ${usdCents(peak.usd)}. Days are UTC.` : 'Stacked by model. Days are UTC.'}
      helpTopic="costs.trend"
      actions={<GrainSwitch />}
    >
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, marginBottom: 6 }}>
        {r.trend.models.map((m) => (
          <span key={m} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: modelColor(r.trend.models, m) }} />
            {m}
          </span>
        ))}
      </div>
      <StackedColumns
        series={r.trend.models}
        colorOf={(m) => modelColor(r.trend.models, m)}
        format={usd}
        data={periods.map((p) => ({
          key: p.start,
          label: label(p.start),
          title: title(p.start),
          values: p.byModel.map((m) => ({ series: m.model, value: m.usd })),
          footnote: `${count(p.calls)} calls${p.callsMissingCost ? ` · ${count(p.callsMissingCost)} without a cost` : ''}`,
        }))}
      />
    </SectionCard>
  );
});

// ── Top agents ──────────────────────────────────────────────────────────────

const Agents = observer(function Agents({ r, onOpen }: { r: AiSpendReport; onOpen: (agentId: number | null) => void }) {
  const max = r.agents[0]?.spendUsd ?? 0;
  return (
    <SectionCard title="Top agents by spend" subtitle="Click an agent to see its days, its models and its costliest runs." helpTopic="costs.agents" noPadding>
      <Table<AiSpendAgentRow>
        size="small"
        rowKey={(a) => String(a.agentId ?? 'none')}
        dataSource={r.agents}
        pagination={r.agents.length > 12 ? { pageSize: 12, size: 'small', showSizeChanger: false } : false}
        onRow={(a) => ({ onClick: () => onOpen(a.agentId), style: { cursor: 'pointer' } })}
        columns={[
          {
            title: 'Agent',
            dataIndex: 'name',
            // Wrap, never truncate: these names are long and are the point of the table.
            render: (_, a) => (
              <span style={{ wordBreak: 'break-word' }}>
                <b>{a.name}</b>
                {a.callsMissingCost > 0 && (
                  <Tooltip title={`${count(a.callsMissingCost)} calls carry no cost — this agent's spend is a floor`}>
                    <WarningFilled style={{ color: colors.amber[500], marginLeft: 6 }} />
                  </Tooltip>
                )}
              </span>
            ),
          },
          {
            title: 'Spend',
            dataIndex: 'spendUsd',
            align: 'right',
            width: 150,
            render: (v: number, a) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                <b style={{ fontVariantNumeric: 'tabular-nums' }}>{usd(v)}</b>
                <MiniBar value={v} max={max} tone="amber" width={44} label={`${a.sharePct}% of spend`} />
              </span>
            ),
          },
          { title: 'Runs', dataIndex: 'runs', align: 'right', width: 80, render: (v: number) => count(v) },
          {
            title: <Tooltip title="Median cost of one run of this agent">Per run</Tooltip>,
            dataIndex: 'medianRunUsd',
            align: 'right',
            width: 85,
            render: (v: number | null) => (v == null ? <Tooltip title={`Fewer than ${r.minRunsForUsual} fully priced runs`}><Text type="secondary">—</Text></Tooltip> : usd(v)),
          },
          { title: 'P95', dataIndex: 'p95RunUsd', align: 'right', width: 80, render: (v: number | null) => (v == null ? <Text type="secondary">—</Text> : usd(v)) },
          {
            title: 'Unusual',
            dataIndex: 'unusualRuns',
            align: 'right',
            width: 85,
            render: (v: number) => (v > 0 ? <Tag color="orange" style={{ marginInlineEnd: 0 }}>{count(v)}</Tag> : <Text type="secondary">0</Text>),
          },
        ]}
      />
    </SectionCard>
  );
});

// ── Cost per run ────────────────────────────────────────────────────────────

const PerRun = observer(function PerRun({ r }: { r: AiSpendReport }) {
  const rs = r.runStats;
  const alertFrom = rs.bands.findIndex((b) => b.fromUsd >= 0.25);
  return (
    <SectionCard title="Cost per run" subtitle="How many runs landed in each price band. The red tail is where loops and oversized prompts hide." helpTopic="costs.per-run">
      {rs.completeRuns === 0 ? (
        <BrandedEmpty size="sm" title="No fully priced runs" description="Every run in this range has at least one call without a cost." />
      ) : (
        <>
          <BandHistogram alertFrom={alertFrom < 0 ? rs.bands.length : alertFrom} bands={rs.bands.map((b) => ({ label: b.label, count: b.runs }))} />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, marginTop: 4 }}>
            <span>Median <b>{usd(rs.medianUsd)}</b></span>
            <span>P95 <b>{usd(rs.p95Usd)}</b></span>
            <span>Highest <b>{usd(rs.maxUsd)}</b></span>
          </div>
          {rs.incompleteRuns > 0 && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              {count(rs.completeRuns)} runs counted · {count(rs.incompleteRuns)} left out because a call in them has no cost.
            </Text>
          )}
        </>
      )}
    </SectionCard>
  );
});

// ── Unusual runs ────────────────────────────────────────────────────────────

const Unusual = observer(function Unusual({ r }: { r: AiSpendReport }) {
  return (
    <SectionCard
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Unusual runs
          <Tag color={r.unusual.count > 0 ? 'red' : 'default'} style={{ marginInlineEnd: 0 }}>{count(r.unusual.count)}</Tag>
        </span>
      }
      subtitle={`Runs that cost more than ${r.unusualMultiple}× what that agent usually costs per run — usually a retry storm, a tool-call loop or a very large document. Highest first${r.unusual.count > r.unusual.top.length ? `; the top ${r.unusual.top.length} are listed` : ''}. Click one to see its calls.`}
      helpTopic="costs.unusual"
      noPadding
    >
      {r.unusual.count === 0 ? (
        <div style={{ padding: 16 }}>
          <BrandedEmpty size="sm" title="Nothing unusual" description={`No run cost more than ${r.unusualMultiple}× its agent's usual in this range.`} />
        </div>
      ) : (
        <RunTable rows={r.unusual.top} showAgent />
      )}
    </SectionCard>
  );
});

/** A quiet "?" for the coverage strip, which is not a SectionCard. */
const SectionHelp = observer(function SectionHelp({ topic }: { topic: string }) {
  const { helpStore } = useStore();
  return (
    <Button type="link" size="small" style={{ paddingInline: 8, height: 'auto' }} onClick={() => helpStore.open(topic)}>
      What do these mean?
    </Button>
  );
});
